import React from 'react';
import { Form, Spinner } from 'react-bootstrap';

export interface LoadingSelectOption {
  value: string | number;
  label: React.ReactNode;
}

type WithLoadingIndicatorProps = {
  showLoadingIndicator?: boolean;
  loadingAriaLabel?: string;
};

interface LoadingInputProps extends Omit<React.ComponentProps<typeof Form.Control>, 'onChange' | 'name'>, WithLoadingIndicatorProps {
  label?: React.ReactNode;
  controlId?: string;
  name: string;
  value?: any;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isLoading?: boolean;
  groupClassName?: string;
  loadingPlaceholder?: string;
  showLoadingPlaceholder?: boolean;
}

export const LoadingInput: React.FC<LoadingInputProps> = ({
  label,
  controlId,
  name,
  value,
  onChange,
  isLoading = false,
  groupClassName,
  placeholder,
  loadingPlaceholder = 'Loading...',
  showLoadingPlaceholder = true,
  showLoadingIndicator = true,
  loadingAriaLabel = 'Loading',
  id,
  disabled,
  style,
  ...rest
}) => {
  const showSpinner = isLoading && showLoadingIndicator;
  const inputId = id ?? controlId;

  return (
    <Form.Group className={groupClassName}>
      {label && <Form.Label htmlFor={inputId}>{label}</Form.Label>}

      <div style={{ position: 'relative' }}>
        <Form.Control
          id={inputId}
          name={name}
          value={value}
          onChange={onChange}
          disabled={disabled}
          readOnly={isLoading || rest.readOnly}
          aria-busy={isLoading || undefined}
          placeholder={isLoading && showLoadingPlaceholder ? loadingPlaceholder : placeholder}
          style={{
            ...(showSpinner ? { paddingRight: '2.25rem' } : null),
            ...style,
          }}
          {...rest}
        />

        {showSpinner && (
          <div
            style={{
              position: 'absolute',
              top: '50%',
              right: '0.75rem',
              transform: 'translateY(-50%)',
              pointerEvents: 'none',
              display: 'flex',
              alignItems: 'center',
            }}
            aria-hidden="true"
          >
            <Spinner animation="border" size="sm" role="status" aria-label={loadingAriaLabel} />
          </div>
        )}

        {isLoading && (
          <span className="visually-hidden" role="status" aria-live="polite">
            {loadingAriaLabel}
          </span>
        )}
      </div>
    </Form.Group>
  );
};

type SelectBlockEvent =
  React.MouseEvent<HTMLSelectElement> | React.KeyboardEvent<HTMLSelectElement> | React.TouchEvent<HTMLSelectElement> | React.WheelEvent<HTMLSelectElement>;

interface LoadingSelectProps extends Omit<React.ComponentProps<typeof Form.Select>, 'onChange' | 'name'>, WithLoadingIndicatorProps {
  label?: React.ReactNode;
  controlId?: string;
  name: string;
  value?: any;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  isLoading?: boolean;
  options: LoadingSelectOption[];
  groupClassName?: string;
  placeholder?: string;
  loadingLabel?: string;
  showLoadingPlaceholderOption?: boolean;
  blockWhenNoOptions?: boolean;
}

export const LoadingSelect: React.FC<LoadingSelectProps> = ({
  label,
  controlId,
  name,
  value,
  onChange,
  isLoading = false,
  options,
  groupClassName,
  placeholder = 'Select...',
  loadingLabel = 'Loading...',
  showLoadingPlaceholderOption = true,
  blockWhenNoOptions = true,
  showLoadingIndicator = true,
  loadingAriaLabel = 'Loading',
  id,
  disabled,
  onMouseDown,
  onClick,
  onKeyDown,
  onTouchStart,
  onWheel,
  style,
  ...rest
}) => {
  const noOptions = !options || options.length === 0;
  const blockSelect = isLoading || (blockWhenNoOptions && noOptions);
  const showSpinner = isLoading && showLoadingIndicator;
  const inputId = id ?? controlId;

  const blockEvent = (e: SelectBlockEvent) => {
    if (!blockSelect) return;
    e.preventDefault();
    e.stopPropagation();
    (e.currentTarget as HTMLSelectElement).blur();
  };

  return (
    <Form.Group className={groupClassName}>
      {label && <Form.Label htmlFor={inputId}>{label}</Form.Label>}

      <div style={{ position: 'relative' }}>
        <Form.Select
          id={inputId}
          name={name}
          value={value ?? ''}
          onChange={blockSelect ? () => {} : onChange}
          disabled={disabled}
          aria-disabled={blockSelect || undefined}
          aria-busy={isLoading || undefined}
          onMouseDown={(e) => {
            blockEvent(e);
            if (!blockSelect) onMouseDown?.(e);
          }}
          onClick={(e) => {
            blockEvent(e);
            if (!blockSelect) onClick?.(e);
          }}
          onTouchStart={(e) => {
            blockEvent(e);
            if (!blockSelect) onTouchStart?.(e);
          }}
          onWheel={(e) => {
            blockEvent(e);
            if (!blockSelect) onWheel?.(e);
          }}
          onKeyDown={(e) => {
            blockEvent(e);
            if (!blockSelect) onKeyDown?.(e);
          }}
          style={{
            ...(showSpinner ? { paddingRight: '2.25rem' } : null),
            ...style,
          }}
          {...rest}
        >
          {blockSelect && showLoadingPlaceholderOption ? (
            <option value="">{isLoading ? loadingLabel : placeholder}</option>
          ) : (
            <>
              {placeholder && <option value="">{placeholder}</option>}
              {options.map((opt) => (
                <option key={String(opt.value)} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </>
          )}
        </Form.Select>

        {showSpinner && (
          <div
            style={{
              position: 'absolute',
              top: '50%',
              right: '0.75rem',
              transform: 'translateY(-50%)',
              pointerEvents: 'none',
              display: 'flex',
              alignItems: 'center',
            }}
            aria-hidden="true"
          >
            <Spinner animation="border" size="sm" role="status" aria-label={loadingAriaLabel} />
          </div>
        )}

        {isLoading && (
          <span className="visually-hidden" role="status" aria-live="polite">
            {loadingAriaLabel}
          </span>
        )}
      </div>
    </Form.Group>
  );
};
